import React from "react";
import { v5 as uuidv5 } from "uuid";
import { Chip, IconButton, Skeleton, Stack, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { ApiError } from "../../../../api/apiFetch";
import { VideoPlatformResource } from "../../../../api/video_platform";
import { DetailField } from "./DetailField";

const GCAL_MEETING_NAMESPACE = "6ba7b814-9dad-11d1-80b4-00c04fd430c8";

interface Props {
  gcalEventId: string;
  refreshKey?: number;
}

export const HostedMeetingFallbackReadOnly: React.FC<Props> = ({
  gcalEventId,
  refreshKey,
}) => {
  const hostedMeetingId = React.useMemo(
    () => uuidv5(gcalEventId, GCAL_MEETING_NAMESPACE),
    [gcalEventId]
  );

  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [useFallback, setUseFallback] = React.useState(false);
  const [fallbackUrl, setFallbackUrl] = React.useState("");
  const [copied, setCopied] = React.useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(fallbackUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    VideoPlatformResource.getMeetingCached(hostedMeetingId)
      .then((m) => {
        if (cancelled) return;
        setUseFallback(m.useFallback ?? false);
        setFallbackUrl(m.fallbackMeetUrl ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hostedMeetingId, refreshKey]);

  if (loading) {
    return <Skeleton variant="text" width={220} height={24} />;
  }

  if (notFound) return null;

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <DetailField
        field="Google Meet Fallback"
        value={useFallback ? "Active" : "Disabled"}
      />
      {useFallback && fallbackUrl && (
        <>
          <Chip
            label={fallbackUrl}
            size="small"
            color="warning"
            variant="outlined"
            component="a"
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            clickable
          />
          <Tooltip title={copied ? "Copied!" : "Copy link"}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? (
                <CheckIcon fontSize="small" color="success" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );
};
